import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { motion } from 'framer-motion';
import SearchableSelect from '../../common/SearchableSelect/SearchableSelect';
import { locationApi, State, City } from '../Auth/utils/LocationApi';
import {
  createPhoneChangeHandler,
  handlePhoneKeyDown,
  formatPhoneNumber,
  getRawPhoneDigits
} from '../Auth/utils/phoneFormatter.js';

interface LegacyContact {
  id: string;
  name: string;
  relationship: string;
  email: string;
  phone: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
}

interface EditLegacyContactModalProps {
  show: boolean;
  onHide: () => void;
  contact?: LegacyContact | null;
  onUpdate: (contactData: Omit<LegacyContact, 'id'>) => void;
}


const validationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .matches(/^[a-zA-Z\s]+$/, 'Only letters and spaces are allowed')
    .required('Primary Legacy Contact Name is required'),
  relationship: Yup.string()
    .trim()
    .matches(/^[a-zA-Z\s]+$/, 'Only letters and spaces are allowed')
    .required('Relationship to You is required'),
  email: Yup.string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email Address is required'),
  phone: Yup.string()
    .required('Phone Number is required')
    .test('phone-validation', 'Phone number must be exactly 10 digits', function(value) {
      if (!value) return false;
      const rawDigits = getRawPhoneDigits(value);
      return rawDigits.length === 10;
    })
    .test('valid-area-code', 'Invalid area code (first digit cannot be 0 or 1)', function(value) {
      if (!value) return false;
      const rawDigits = getRawPhoneDigits(value);
      if (rawDigits.length < 3) return false;
      const areaCode = rawDigits.substring(0, 3);
      return areaCode[0] !== '0' && areaCode[0] !== '1';
    }),
  address1: Yup.string()
    .trim()
    .required('Address 1 is required'),
  address2: Yup.string().nullable(),
  city: Yup.string()
    .trim()
    .required('City is required'),
  state: Yup.string()
    .required('State is required'),
  zip: Yup.string()
    .matches(/^\d{5}$/, 'Zip code must be 5 digits')
    .required('Zip code is required')
});

const EditLegacyContactModal: React.FC<EditLegacyContactModalProps> = ({
  show,
  onHide,
  contact,
  onUpdate
}) => {
  const isEditing = !!contact;
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Load states on component mount
  useEffect(() => {
    loadStates();
  }, []);

  // Load cities when contact changes (for editing)
  useEffect(() => {
    if (contact?.state && states.length > 0) {
      const selectedState = states.find(state => state.name === contact.state);
      if (selectedState) {
        loadCitiesByStateId(selectedState._id);
      }
    } else {
      setCities([]);
    }
  }, [contact, states]);

  const loadStates = async () => {
    try {
      setLoading(true);
      const response = await locationApi.getAllStates();
      if (response.status === 'Success' && response.states) {
        setStates(response.states);
      }
    } catch (error) {
      console.error('Error loading states:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load cities by state ID
  const loadCitiesByStateId = async (stateId: string) => {
    if (!stateId) {
      setCities([]);
      return;
    }

    try {
      setLoadingCities(true);
      const response = await locationApi.getCitiesByStateId(stateId);

      if (response.status === 'Success' && response.cities) {
        setCities(response.cities);
      } else {
        setCities([]);
      }
    } catch (error) {
      console.error('Error loading cities:', error);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  // Handle state change
  const handleStateChange = (stateId: string) => {
    const selectedState = states.find(state => state._id === stateId);

    if (selectedState) {
      formik.setFieldValue('state', selectedState.name);
      formik.setFieldValue('city', ''); // Reset city when state changes
      loadCitiesByStateId(stateId);
    } else {
      formik.setFieldValue('state', '');
      formik.setFieldValue('city', '');
      setCities([]);
    }
  };

  const inputStyle = {
    borderRadius: '8px',
    border: '2px solid #e9ecef',
    padding: '12px 16px',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    backgroundColor: '#fff'
  };

  const labelStyle = {
    fontWeight: '600',
    color: '#495057',
    marginBottom: '8px'
  };

  const handleInputFocus = (e: any) => {
    e.target.style.border = '2px solid #3957A0';
    e.target.style.boxShadow = '0 0 0 0.2rem rgba(57, 87, 160, 0.25)';
  };

  const handleInputBlur = (e: any, formikBlur: any) => {
    formikBlur(e);
    e.target.style.border = '2px solid #e9ecef';
    e.target.style.boxShadow = 'none';
  };

  const formik = useFormik({
    initialValues: {
      name: contact?.name || '',
      relationship: contact?.relationship || '',
      email: contact?.email || '',
      phone: contact?.phone ? formatPhoneNumber(contact.phone) : '+1',
      address1: contact?.address1 || '',
      address2: contact?.address2 || '',
      city: contact?.city || '',
      state: contact?.state || '',
      zip: contact?.zip || ''
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // Send raw phone digits without +1 formatting
        const dataToSubmit = {
          ...values,
          phone: getRawPhoneDigits(values.phone)
        };
        await onUpdate(dataToSubmit);
      } catch (error) {
        console.error('Error updating legacy contact:', error);
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Create phone change handler
  const handlePhoneChange = createPhoneChangeHandler(formik, 'phone');

  const handleClose = () => {
    formik.resetForm();
    setCities([]); // Reset cities when modal closes
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      className="edit-legacy-contact-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <Modal.Header closeButton style={{backgroundColor: '#f8f9fa', borderBottom: '2px solid #3957A0'}}>
          <Modal.Title style={{color: '#3957A0', fontWeight: 'bold', fontSize: '1.5rem'}}>
            <i className="fas fa-users me-2" style={{color: '#D7A360'}}></i>
            {isEditing ? 'Edit Legacy Contact' : 'Add Legacy Contact'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{padding: '2rem', backgroundColor: '#fff'}}>
          <Form onSubmit={formik.handleSubmit}>
            <div className="form-section mb-4">
             
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Full Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={(e) => handleInputBlur(e, formik.handleBlur)}
                      onFocus={handleInputFocus}
                      isInvalid={formik.touched.name && !!formik.errors.name}
                      placeholder="Enter contact's full name"
                      style={inputStyle}
                    />
                    <Form.Control.Feedback type="invalid" style={{fontSize: '13px', marginTop: '5px'}}>
                      {formik.errors.name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Relationship to You *</Form.Label>
                    <Form.Control
                      type="text"
                      name="relationship"
                      value={formik.values.relationship}
                      onChange={formik.handleChange}
                      onBlur={(e) => handleInputBlur(e, formik.handleBlur)}
                      onFocus={handleInputFocus}
                      isInvalid={formik.touched.relationship && !!formik.errors.relationship}
                      placeholder="e.g., Spouse, Child, Friend, etc."
                      style={inputStyle}
                    />
                    <Form.Control.Feedback type="invalid" style={{fontSize: '13px', marginTop: '5px'}}>
                      {formik.errors.relationship}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="form-section mb-4">
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Email Address *</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={(e) => handleInputBlur(e, formik.handleBlur)}
                      onFocus={handleInputFocus}
                      isInvalid={formik.touched.email && !!formik.errors.email}
                      placeholder="Enter email address"
                      style={inputStyle}
                    />
                    <Form.Control.Feedback type="invalid" style={{fontSize: '13px', marginTop: '5px'}}>
                      {formik.errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Phone Number *</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formik.values.phone}
                      onChange={handlePhoneChange}
                      onKeyDown={handlePhoneKeyDown}
                      onBlur={(e) => handleInputBlur(e, formik.handleBlur)}
                      onFocus={handleInputFocus}
                      isInvalid={formik.touched.phone && !!formik.errors.phone}
                      placeholder="+1 (555) 555-5555"
                      style={inputStyle}
                    />
                    <Form.Control.Feedback type="invalid" style={{fontSize: '13px', marginTop: '5px'}}>
                      {formik.errors.phone}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="form-section mb-4">
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Address 1 *</Form.Label>
                    <Form.Control
                      type="text"
                      name="address1"
                      value={formik.values.address1}
                      onChange={formik.handleChange}
                      onBlur={(e) => handleInputBlur(e, formik.handleBlur)}
                      onFocus={handleInputFocus}
                      isInvalid={formik.touched.address1 && !!formik.errors.address1}
                      placeholder="Enter street address"
                      style={inputStyle}
                    />
                    <Form.Control.Feedback type="invalid" style={{fontSize: '13px', marginTop: '5px'}}>
                      {formik.errors.address1}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Address 2</Form.Label>
                    <Form.Control
                      type="text"
                      name="address2"
                      value={formik.values.address2}
                      onChange={formik.handleChange}
                      onBlur={(e) => handleInputBlur(e, formik.handleBlur)}
                      onFocus={handleInputFocus}
                      placeholder="Apartment, suite, etc. (optional)"
                      style={inputStyle}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>State *</Form.Label>
                    <SearchableSelect
                      options={states.map(state => ({
                        value: state._id,
                        label: state.name
                      }))}
                      value={states.find(state => state.name === formik.values.state)?._id || ''}
                      placeholder="Select State"
                      onChange={handleStateChange}
                      disabled={loading}
                      loading={loading}
                      loadingText="Loading states..."
                      isInvalid={!!(formik.touched.state && formik.errors.state)}
                      searchPlaceholder="Search states..."
                      noOptionsText="No states found"
                    />
                    {formik.touched.state && formik.errors.state && (
                      <small style={{
                        color: '#dc3545',
                        display: 'block',
                        marginTop: '4px',
                        fontSize: '13px'
                      }}>
                        {formik.errors.state}
                      </small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>City *</Form.Label>
                    <SearchableSelect
                      options={cities.map(city => ({
                        value: city.name,
                        label: city.name
                      }))}
                      value={formik.values.city}
                      placeholder={
                        !formik.values.state
                          ? 'Select State First'
                          : 'Select City'
                      }
                      onChange={(value) => {
                        formik.setFieldValue('city', value);
                      }}
                      disabled={loading || loadingCities || !formik.values.state}
                      loading={loadingCities}
                      loadingText="Loading cities..."
                      isInvalid={!!(formik.touched.city && formik.errors.city)}
                      searchPlaceholder="Search cities..."
                      noOptionsText="No cities found"
                    />
                    {formik.touched.city && formik.errors.city && (
                      <small style={{
                        color: '#dc3545',
                        display: 'block',
                        marginTop: '4px',
                        fontSize: '13px'
                      }}>
                        {formik.errors.city}
                      </small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Zip Code *</Form.Label>
                    <Form.Control
                      type="text"
                      name="zip"
                      value={formik.values.zip}
                      onChange={formik.handleChange}
                      onBlur={(e) => handleInputBlur(e, formik.handleBlur)}
                      onFocus={handleInputFocus}
                      isInvalid={formik.touched.zip && !!formik.errors.zip}
                      placeholder="Enter zip code"
                      style={inputStyle}
                    />
                    <Form.Control.Feedback type="invalid" style={{fontSize: '13px', marginTop: '5px'}}>
                      {formik.errors.zip}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="d-flex justify-content-center pt-3" style={{borderTop: '1px solid #e9ecef'}}>
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={formik.isSubmitting}
                style={{
                  marginRight: '12px',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  border: '2px solid #6c757d',
                  backgroundColor: 'transparent',
                  color: '#6c757d'
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={formik.isSubmitting || !formik.dirty || !formik.isValid}
                style={{
                  padding: '12px 32px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  backgroundColor: '#3957A0',
                  border: '2px solid #3957A0',
                  color: 'white',
                  minWidth: '140px'
                }}
              >
                {formik.isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    {isEditing ? 'Updating...' : 'Adding...'}
                  </>
                ) : (
                  isEditing ? 'Update Contact' : 'Add Contact'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </motion.div>
    </Modal>
  );
};

export default EditLegacyContactModal;